import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import { getState, select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';

import useHasMitIdDataContext from '../../../hooks/useHasMitIdDataContext';
import useMitIdDataContext from '../../../hooks/useMitIdDataContext';

export interface FeatureFlagData {
  data: { features: Record<FeatureFlag, boolean> };
}

export interface CountryStateData {
  values: { country: string; state?: string };
}

export enum Guard {
  AuthenticationCheck = 'authenticationCheck',
  AutoTrigerRmf = 'autoTrigerRmf',
  ProvinceCheck = 'provinceCheck',
  SignupMitIdDataPresent = 'signupMitIdDataPresent',
  SignupConsentPreferencesEnabled = 'signupConsentPreferencesEnabled',
  SignupCountryAvailable = 'signupCountryAvailable',
  SignupRmfEnabled = 'signupRmfEnabled',
  SingleScreen = 'singleScreen',
  SkipSignupCountry = 'skipSignupCountry',
  SignupMitIdData = 'signupMitIdData'
}

export const isSiteCAON = (site: Sites): boolean => site === Sites.CA_ON;
export const isSiteCOM = (site: Sites): boolean => site === Sites.COM;

const provinceCAON = 'ON';

const useSignupGuards = () => {
  const site = useSelector(select.cms.getSiteId);
  const isAuthenticated: boolean = useSelector(select.session.isAuthenticated);
  const { country, region } = useSelector(select.location.getQuery);
  const authenticationCheck = useCallback((): boolean => isAuthenticated, [isAuthenticated]);
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const hasMitIdData = useMitIdDataContext('/signup/');
  const hasSignupMitIdDataContext = useHasMitIdDataContext();

  const checkSignupRmfFeature = () => select.feature.check(getState(), FeatureFlag.SwcSignupRmf);

  const showSingleScreen = () => select.feature.check(getState(), FeatureFlag.SwcSignupOnePage);

  const signupConsentPreferencesEnabled = () =>
    select.feature.check(getState(), FeatureFlag.SwcSignupConsentPreferences);

  const skipSignupCountry = useCallback(() => {
    if (isSiteUS) return true;
    return (
      country === Country.Canada &&
      ((isSiteCOM(site) && region !== provinceCAON) ||
        (isSiteCAON(site) && region === provinceCAON))
    );
  }, [country, isSiteUS, region, site]);

  const checkCountryAvailable = useCallback(
    ({ data: { isCountryAvailable } }: { data: { isCountryAvailable: boolean } }) =>
      isCountryAvailable,
    []
  );

  const checkRmfAutoTrigger = () => select.feature.check(getState(), FeatureFlag.WrmfAcAutotrigger);

  const provinceCheck = useCallback(
    ({ values: { country, state } }: CountryStateData) =>
      country === Country.Canada &&
      ((isSiteCOM(site) && state === provinceCAON) || (isSiteCAON(site) && state !== provinceCAON)),
    [site]
  );

  const signupMitIdDataPresent = () => hasMitIdData;
  const signupMitIdData = ({ data }: WizardContext) => hasSignupMitIdDataContext({ data });

  return {
    [Guard.AuthenticationCheck]: authenticationCheck,
    [Guard.AutoTrigerRmf]: checkRmfAutoTrigger,
    [Guard.ProvinceCheck]: provinceCheck,
    [Guard.SignupMitIdDataPresent]: signupMitIdDataPresent,
    [Guard.SignupConsentPreferencesEnabled]: signupConsentPreferencesEnabled,
    [Guard.SignupCountryAvailable]: checkCountryAvailable,
    [Guard.SignupRmfEnabled]: checkSignupRmfFeature,
    [Guard.SingleScreen]: showSingleScreen,
    [Guard.SkipSignupCountry]: skipSignupCountry,
    [Guard.SignupMitIdData]: signupMitIdData
  };
};

export default useSignupGuards;
